import { AnimatePresence, motion } from 'framer-motion'
import { forwardRef, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

const isMobile = () => typeof window !== 'undefined' && window.innerWidth < 768
const STAYS_ACCENT = '#80C7E2'
const STAYS_ACCENT_SOFT = '#F2FBFE'
const STAYS_ACCENT_SOFT_HOVER = '#E8F7FC'
const STAYS_TEXT_DARK = '#000D33'
const STAYS_TEXT_MUTED = '#55656F'
const PANEL_EASE = [0.22, 1, 0.36, 1]
const PANEL_EXIT_EASE = [0.4, 0, 1, 1]
const PANEL_LAYOUT_TRANSITION = {
  type: 'spring',
  stiffness: 420,
  damping: 34,
  mass: 0.82,
}

const desktopPanelMotion = {
  initial: { opacity: 0, y: 12, scale: 0.96, filter: 'blur(8px)' },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    filter: 'blur(0px)',
    transition: { duration: 0.24, ease: PANEL_EASE },
  },
  exit: {
    opacity: 0,
    y: 8,
    scale: 0.98,
    filter: 'blur(4px)',
    transition: { duration: 0.16, ease: PANEL_EXIT_EASE },
  },
}

const mobileBackdropMotion = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.18, ease: 'easeOut' } },
  exit: { opacity: 0, transition: { duration: 0.14, ease: 'easeIn' } },
}

const mobileSheetMotion = {
  initial: { opacity: 0, y: 42, scale: 0.98 },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.34, ease: PANEL_EASE },
  },
  exit: {
    opacity: 0,
    y: 28,
    scale: 0.99,
    transition: { duration: 0.2, ease: PANEL_EXIT_EASE },
  },
}

const GUEST_TYPES = [
  {
    key: 'adults',
    label: 'Adults',
    description: 'Ages 13 or above',
    minValue: 1,
  },
  {
    key: 'children',
    label: 'Children',
    description: 'Ages 3-12',
    minValue: 0,
  },
  // {
  //   key: 'infants',
  //   label: 'Infants',
  //   description: 'Under 2',
  //   minValue: 0
  // },
  {
    key: 'pets',
    label: 'Pets',
    description: "service animals don't count",
    minValue: 0,
  },
]

const GuestCounterButton = ({
  onClick,
  disabled,
  children,
  className = '',
  ariaLabel,
}) => {
  const [isHovered, setIsHovered] = useState(false)
  const [isPressed, setIsPressed] = useState(false)

  return (
    <button
      type='button'
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={`h-8 w-8 cursor-pointer rounded-full border flex items-center justify-center text-[17px] disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      style={{
        borderColor: isHovered ? STAYS_ACCENT : '#CFEAF4',
        backgroundColor: isPressed ? STAYS_ACCENT : isHovered ? STAYS_ACCENT_SOFT : '#FFFFFF',
        color: isPressed ? '#FFFFFF' : STAYS_TEXT_DARK,
        boxShadow: isHovered ? '0 6px 14px rgba(128, 199, 226, 0.18)' : '0 2px 6px rgba(10, 28, 44, 0.04)',
        transform: isPressed ? 'scale(0.96)' : isHovered ? 'translateY(-1px)' : 'translateY(0)',
        transition: 'background-color 150ms ease, border-color 150ms ease, color 150ms ease, box-shadow 150ms ease, transform 150ms ease',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false)
        setIsPressed(false)
      }}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
    >
      {children}
    </button>
  )
}

const GuestRow = ({ type, count, onUpdate, maxGuests, totalGuests }) => {
  // Calculate if increment should be disabled
  const isIncrementDisabled = () => {
    if (type.key === 'pets') {
      return count >= 2 // Max 2 pets
    }
    if (type.key === 'adults' || type.key === 'children') {
      return totalGuests >= maxGuests // Max guests reached
    }
    return false // Infants don't count towards max guests
  }

  return (
    <div
      className='flex min-h-[74px] items-center justify-between gap-3 rounded-[14px] px-0 py-3'
      style={{ backgroundColor: '#FFFFFF' }}
    >
      <div className='flex-1 pr-2.5'>
        <div className='font-semibold text-[14px] leading-5' style={{ color: STAYS_TEXT_DARK }}>
          {type.label}
        </div>
        <div className='mt-0.5 text-[12px] leading-[17px]' style={{ color: STAYS_TEXT_MUTED }}>
          {type.description}
        </div>
      </div>
      <div
        className='flex items-center gap-2 rounded-full border px-2 py-1'
        style={{
          backgroundColor: '#F7FCFE',
          borderColor: '#DCECF3',
        }}
      >
        <GuestCounterButton
          onClick={() => onUpdate(type.key, -1)}
          disabled={count <= type.minValue}
          ariaLabel={`Decrease ${type.label.toLowerCase()} count`}
        >
          -
        </GuestCounterButton>
        <span className='w-6 text-center font-semibold text-[14px]' style={{ color: STAYS_TEXT_DARK }}>
          {count}
        </span>
        <GuestCounterButton
          onClick={() => onUpdate(type.key, 1)}
          disabled={isIncrementDisabled()}
          ariaLabel={`Increase ${type.label.toLowerCase()} count`}
        >
          +
        </GuestCounterButton>
      </div>
    </div>
  )
}

const GuestSelector = forwardRef(
  (
    {
      isOpen,
      guestCounts,
      onUpdate,
      onToggle,
      getDisplayText,
      guestTypes = GUEST_TYPES,
      className = '',
      textStyle = {},
      dropdownClassName = 'top-[48px]', // Control top position of dropdown
      showDropdownStyling = false, // Controls whether to show dropdown button styling
      maxGuests = 10, // Maximum number of guests allowed
      totalGuests = 0, // Current total guests (adults + children)
      isListingPage,
      desktopPanelLayoutId,
      mobileSheetLayoutId,
      showHeader = true,
    },
    ref,
  ) => {
    const displayText = getDisplayText()
    const [isMounted, setIsMounted] = useState(false)

    const desktopPanelAnimationProps = desktopPanelLayoutId
      ? {
          layout: true,
          transition: { layout: PANEL_LAYOUT_TRANSITION },
        }
      : {
          initial: desktopPanelMotion.initial,
          animate: desktopPanelMotion.animate,
          exit: desktopPanelMotion.exit,
        }

    const mobileSheetAnimationProps = mobileSheetLayoutId
      ? {
          layout: true,
          transition: { layout: PANEL_LAYOUT_TRANSITION },
        }
      : {
          initial: mobileSheetMotion.initial,
          animate: mobileSheetMotion.animate,
          exit: mobileSheetMotion.exit,
        }

    useEffect(() => {
      setIsMounted(true)
    }, [])

    // Lock body scroll when mobile bottom sheet is open
    useEffect(() => {
      if (isOpen && isMobile()) {
        document.body.classList.add('bottom-sheet-open')
      } else {
        document.body.classList.remove('bottom-sheet-open')
      }
      return () => document.body.classList.remove('bottom-sheet-open')
    }, [isOpen])

    return (
      <div className={`w-full relative ${className}`} ref={ref}>
        {/* Desktop Display */}
        {showDropdownStyling ? (
          <div
            className='hidden md:flex items-center justify-between cursor-pointer select-none bg-white hover:bg-gray-50 rounded-lg px-3 py-1 transition-all duration-200'
            onClick={onToggle}
            title={displayText}
          >
            {/* <span
              className={`text-[13px] font-medium font-sans truncate flex-1 ${
                displayText === 'Add guests' ? 'text-[#898989]' : 'text-black'
              }`}
              title={displayText}
              style={{ ...textStyle }}
            >
              {displayText}
            </span> */}
            <svg
              className={`w-4 h-4 text-gray-500 transition-transform duration-200 ml-3 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M19 9l-7 7-7-7'
              />
            </svg>
          </div>
        ) : (
          <div
            className={`hidden md:block cursor-pointer select-none text-[14px] font-Poppins font-medium truncate ${
              isListingPage ? 'text-[#191919]' : 'text-[#898989]'
            }`}
            onClick={onToggle}
            title={displayText}
            style={{ maxWidth: '100%', ...textStyle }}
          >
            {displayText}
          </div>
        )}

        {/* Mobile Display */}
        {showDropdownStyling ? (
          <div
            className='md:hidden flex items-center justify-between cursor-pointer select-none bg-white hover:bg-gray-50 rounded-lg px-4 py-3 transition-all duration-200'
            onClick={onToggle}
          >
            <span
              className={`md:text-[13px] font-medium font-sans truncate flex-1 text-[14px] ${
                displayText === 'Add guests'
                  ? 'text-[#191919]'
                  : 'text-[#898989]'
              }`}
              title={displayText}
              style={{ ...textStyle }}
            >
              {displayText}
            </span>
            <svg
              className={`w-4 h-4 text-gray-500 transition-transform duration-200 ml-3 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M19 9l-7 7-7-7'
              />
            </svg>
          </div>
        ) : (
          <div className='md:hidden cursor-pointer select-none' onClick={onToggle}>
            <span
              className={`text-[14px] font-medium font-sans truncate block ${
                displayText === 'Add guests'
                  ? 'text-[#898989]'
                  : 'text-[#191919]'
              }`}
              title={displayText}
              style={{ maxWidth: '100%', ...textStyle }}
            >
              {displayText}
            </span>
          </div>
        )}

        {/* Dropdown */}
        <AnimatePresence initial={false}>
          {isOpen && (
          <>
            {/* Desktop dropdown */}
            <motion.div
              layoutId={desktopPanelLayoutId}
              className={`hidden md:block absolute ${dropdownClassName} bg-white shadow-xl px-4 py-0 rounded-[24px] border z-[99999] min-w-80 w-full right-0`}
              style={{
                borderColor: '#D8F1F8',
                background: 'linear-gradient(180deg, #FFFFFF 0%, #FBFEFF 100%)',
                boxShadow: '0 24px 56px rgba(10, 28, 44, 0.14)',
              }}
              {...desktopPanelAnimationProps}
            >
              {showHeader && (
                <div className='border-b pt-4 pb-3' style={{ borderColor: '#EAF6FB' }}>
                  <div className='text-[12px] font-semibold' style={{ color: STAYS_TEXT_DARK }}>
                    Guests
                  </div>
                  <div className='text-[13px] leading-5' style={{ color: STAYS_TEXT_MUTED }}>
                    Build your travel party
                  </div>
                </div>
              )}
              <div className='space-y-0'>
                {guestTypes.map((type) => (
                  <GuestRow
                    key={type.key}
                    type={type}
                    count={guestCounts[type.key]}
                    onUpdate={onUpdate}
                    maxGuests={maxGuests}
                    totalGuests={totalGuests}
                  />
                ))}
              </div>
            </motion.div>

            {/* Mobile bottom sheet — portaled to body */}
            {isMounted &&
              createPortal(
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <div className='md:hidden' data-guest-mobile-portal>
                      <motion.div
                        className='fixed inset-0 z-[99998]'
                        style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
                        initial={mobileBackdropMotion.initial}
                        animate={mobileBackdropMotion.animate}
                        exit={mobileBackdropMotion.exit}
                        onClick={onToggle}
                      />
                      <div className='fixed bottom-0 left-0 right-0 z-[99999]'>
                        <motion.div
                          layoutId={mobileSheetLayoutId}
                          className='bg-white rounded-t-[28px] shadow-2xl px-4 pt-4 pb-6'
                          style={{ boxShadow: '0 -12px 36px rgba(10, 28, 44, 0.18)' }}
                          {...mobileSheetAnimationProps}
                        >
                          <div className={`flex justify-end ${showHeader ? 'mb-3 pb-3 border-b' : 'mb-2'}`} style={showHeader ? { borderColor: '#EAF6FB' } : undefined}>
                            <button
                              type='button'
                              onClick={onToggle}
                              className='text-[#898989] text-sm font-medium cursor-pointer'
                            >
                              Done
                            </button>
                          </div>
                          <div className='space-y-0'>
                            {guestTypes.map((type) => (
                              <GuestRow
                                key={type.key}
                                type={type}
                                count={guestCounts[type.key]}
                                onUpdate={onUpdate}
                                maxGuests={maxGuests}
                                totalGuests={totalGuests}
                              />
                            ))}
                          </div>
                        </motion.div>
                      </div>
                    </div>
                  )}
                </AnimatePresence>,
                document.body,
              )}
          </>
          )}
        </AnimatePresence>
      </div>
    )
  },
)

GuestSelector.displayName = 'GuestSelector'

export default GuestSelector
